import type { AnchorHTMLAttributes } from "react";
import { ErrorBoundary } from "react-error-boundary";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

/**
 * Render a help article body as sanitized Markdown.
 *
 * Security posture (per `.claude/rules/security.md` and AC-S-4):
 * - Uses `react-markdown`'s default sanitizing path. No `rehypePlugins` and
 *   specifically NO `rehype-raw` — raw HTML inside an article body must render
 *   as literal text, never as DOM. The `<MarkdownArticle — no html injection>`
 *   test asserts a `<script>` tag survives as text.
 * - External links (any `href` starting with `http`) get
 *   `target="_blank" rel="noopener noreferrer"` via the `ExternalLink` override
 *   so a click can't reach back into `window.opener`.
 * - The whole render is wrapped in `react-error-boundary` so a malformed
 *   article surfaces the dialog's `error` state instead of a white screen.
 *   The fallback intentionally keeps the message terse — Simon's email lives
 *   in the dialog body, not here.
 */
export function MarkdownArticle({ markdown }: { markdown: string }) {
	return (
		<ErrorBoundary fallback={<MarkdownErrorFallback />}>
			<div className="prose prose-sm max-w-none text-[#1e1e20]">
				<ReactMarkdown remarkPlugins={[remarkGfm]} components={{ a: ExternalLink }}>
					{markdown}
				</ReactMarkdown>
			</div>
		</ErrorBoundary>
	);
}

function MarkdownErrorFallback() {
	return (
		<div
			role="alert"
			className="rounded border border-[#fecaca] bg-[#fef2f2] p-3 text-sm text-[#991b1b]"
		>
			This article failed to render.
		</div>
	);
}

function ExternalLink({ href, children, ...rest }: AnchorHTMLAttributes<HTMLAnchorElement>) {
	const isExternal = typeof href === "string" && /^https?:\/\//i.test(href);
	if (isExternal) {
		return (
			<a href={href} target="_blank" rel="noopener noreferrer" {...rest}>
				{children}
			</a>
		);
	}
	return (
		<a href={href} {...rest}>
			{children}
		</a>
	);
}
